import { useAuth0 } from '@auth0/auth0-react'

import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import ExitToAppIcon from '@mui/icons-material/ExitToApp'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import TodayIcon from '@mui/icons-material/Today'
import Box from '@mui/material/Box'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Typography from '@mui/material/Typography'

import packageInfo from '../../../package.json'
import { useAuthenticatedUser } from '../../auth/hooks/useAuthenticatedUser'
import { getVersion } from '../../config'
import { getAuth0Options } from '../../gateways/auth0'
import { Link } from '../../router/components/Link'
import { useLocation } from '../../router/hooks/useLocation'
import { useNavigate } from '../../router/hooks/useNavigate'
import { CapoLogo } from '../../theme/components/CapoLogo'

import { AansluitingMenu } from './AansluitingMenu'
import { HoofdleidingMenu } from './HoofdleidingMenu'
import { HoofdnetMenu } from './HoofdnetMenu'
import { KetensamenwerkingMenu } from './KetensamenwerkingMenu'

export { Menu, SIDEBAR_WIDTH }

const SIDEBAR_WIDTH = 250

type Props = {
  onItemClick?: () => void
  showLogo?: boolean
}

const Menu = ({ onItemClick, showLogo = false }: Props) => {
  const { logout } = useAuth0()
  const options = getAuth0Options()
  const returnTo = options.returnTo || '/'
  const sha = getVersion()
  const { canViewBeheer, canViewProjecten } = useAuthenticatedUser()
  const navigate = useNavigate()
  const { pathname } = useLocation()

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
        minHeight: '100%',
        width: SIDEBAR_WIDTH,
      }}
    >
      <Box sx={{ width: '100%' }} role="presentation">
        {showLogo && (
          <CapoLogo
            onClick={
              pathname !== '/home'
                ? () => {
                    navigate('/home')
                    onItemClick?.()
                  }
                : undefined
            }
            sx={{ margin: '20px 25px' }}
          />
        )}

        <AansluitingMenu onItemClick={onItemClick} />

        <HoofdleidingMenu onItemClick={onItemClick} />

        <HoofdnetMenu onItemClick={onItemClick} />

        <KetensamenwerkingMenu onItemClick={onItemClick} />

        <List>
          {canViewBeheer && (
            <ListItemButton component={Link} to="/beheer" onClick={onItemClick}>
              <ListItemIcon sx={{ color: 'black' }}>
                <TodayIcon />
              </ListItemIcon>
              <ListItemText primary="Beheer" />
            </ListItemButton>
          )}

          {canViewProjecten && (
            <ListItemButton
              component={Link}
              to="/planning"
              onClick={onItemClick}
            >
              <ListItemIcon sx={{ color: 'black' }}>
                <CalendarMonthIcon />
              </ListItemIcon>
              <ListItemText primary="Geblokkeerde weken" />
            </ListItemButton>
          )}

          <ListItemButton
            component={'a'}
            rel="noopener noreferrer"
            href="https://mijnaansluiting.atlassian.net/wiki/spaces/Wiki/pages/39911528/CAPO+UI+Handleiding"
            target="_blank"
            data-testid="handleidingLink"
          >
            <ListItemIcon sx={{ color: 'black' }}>
              <MenuBookIcon />
            </ListItemIcon>
            <ListItemText primary="Handleiding" />
          </ListItemButton>

          <ListItemButton
            onClick={() => logout({ logoutParams: { returnTo } })}
          >
            <ListItemIcon sx={{ color: 'black' }}>
              <ExitToAppIcon />
            </ListItemIcon>
            <ListItemText primary="Uitloggen" />
          </ListItemButton>
        </List>
      </Box>

      <Typography
        variant="caption"
        sx={{
          my: '10px',
        }}
      >{`v${packageInfo.version}${sha}`}</Typography>
    </Box>
  )
}
